import { AfterViewInit, Component, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  BILLING_CYCLE_LABELS,
  IBillingCycle,
  IBrandBillingCycle,
  ProductsSubprojectService,
  VendorProductWithSubprojectProduct,
} from 'src/app/services/products-subproject/products-subproject.service';
import { BillingCycleModalComponent } from '../../../../modals/billing-cycle-modal/billing-cycle-modal.component';

@Component({
    selector: 'app-products-table',
    templateUrl: './products-table.component.html',
    styleUrls: ['./products-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductsTableComponent implements AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @Input() billingCycles: IBillingCycle[] = [];
  @Input() set products(value: VendorProductWithSubprojectProduct[]) {
    console.log('ProductsTableComponent products set', value);
    this.tableDataSource.data = value.sort((a, b) =>
      a.productName && b.productName ? a.productName.localeCompare(b.productName) : 0,
    );
    this.tableDataSource.sort = this.sort;
  }
  @Input() set brandId(value: string) {
    if (value) {
      this._brandId = value;
      this.loadBrandsBillingCycles();
    }
  }
  get brandId(): string {
    return this._brandId;
  }
  private _brandId: string = '';
  public columnNames = ['productName', 'stripeProductId', 'billingCycle', 'action'];
  public tableDataSource = new MatTableDataSource<VendorProductWithSubprojectProduct>();
  public brandsBillingCycle: IBrandBillingCycle[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly productsSubprojectService: ProductsSubprojectService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngAfterViewInit() {
    this.tableDataSource.sort = this.sort;
  }

  private getBillingCycle(
    productId: string,
  ): (IBillingCycle & { billingDay: number | null }) | null {
    const brandBillingCycle = this.brandsBillingCycle.find(
      (brandBC) => brandBC.productId === productId,
    );
    if (brandBillingCycle) {
      const billingCycle = this.billingCycles.find(
        (bc) => bc.id === brandBillingCycle.billingCycleId,
      );
      if (billingCycle) {
        return { ...billingCycle, billingDay: brandBillingCycle.billingDay };
      }
    }
    return null;
  }

  public getBillingCycleLabel(productId: string): string {
    const billingCycle = this.getBillingCycle(productId);
    if (billingCycle)
      return BILLING_CYCLE_LABELS[billingCycle.type as keyof typeof BILLING_CYCLE_LABELS];
    return 'Default Billing';
  }

  private async loadBrandsBillingCycles() {
    const brandsBillingCycleResponse =
      await this.productsSubprojectService.getBrandsBillingCycleByBrandId(this.brandId);
    if (brandsBillingCycleResponse.success) {
      this.brandsBillingCycle = brandsBillingCycleResponse.data;
    } else {
      this.snackBar.open('Failed to load brand billing cycles', 'Close', { duration: 3000 });
    }
  }

  private async saveFunction(data: {
    resourceId: string;
    productId: string;
    billingCycleId: number | null;
    billingDay?: number;
  }) {
    if (data.billingCycleId === null) {
      return this.productsSubprojectService.deleteBrandBillingCycle(
        data.productId,
        data.resourceId,
      );
    } else {
      return this.productsSubprojectService.createOrUpdateBrandBillingCycle(
        data.productId,
        data.resourceId,
        data.billingCycleId,
        data.billingDay,
      );
    }
  }

  public openBillingCycleModal(product: VendorProductWithSubprojectProduct) {
    const billingCycle = this.getBillingCycle(product.subprojectProductId as string);
    const dialogRef = this.dialog.open(BillingCycleModalComponent, {
      data: {
        billingCycles: this.billingCycles,
        productId: product.subprojectProductId,
        resourceId: this.brandId,
        saveFunction: this.saveFunction.bind(this),
        billingCycleInfo: {
          billingCycleId: billingCycle ? billingCycle.id : null,
          billingDay: billingCycle ? billingCycle.billingDay : undefined,
        },
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadBrandsBillingCycles();
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.tableDataSource.filter = filterValue.trim();
  }
}
